import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectOrganization } from "../store/slices/authUserSlice";
import StudioInterviewCard from "./StudioInterviewCard";
import VoiceNoteModal from "./VoiceNoteModal";
import AddLinkModal from "./AddLinkModal";
import AddTextDocumentModal from "./AddTextDocumentModal";
import { ReactComponent as MicIcon } from "../assets/icons/mic.svg";
import { ReactComponent as LinkIcon } from "../assets/icons/link.svg";
import { ReactComponent as TextIcon } from "../assets/icons/text.svg";

enum IngredientType {
  VOICE_NOTE = 'voice_note',
  LINK = 'link',
  TEXT = 'text'
}

const Requests: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCard, setActiveCard] = useState<IngredientType | null>(null);
  const navigate = useNavigate();
  const organization = useSelector(selectOrganization);
  const hasInterviews = !!organization?.feature_flags?.content_interviews;

  useEffect(() => {
    const params = Object.values(IngredientType);
    const activeParam = params.find(param => searchParams.get(param) === "1");
    setActiveCard(activeParam || null);
  }, [searchParams]);

  const handleOptionOpen = (option: IngredientType) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (activeCard) {
      newSearchParams.delete(activeCard);
    }
    setActiveCard(option);
    newSearchParams.set(option, "1");
    
    setSearchParams(newSearchParams);
  };

  const handleOptionClose = () => {
    if (activeCard) {
      const newSearchParams = new URLSearchParams(searchParams);
      setActiveCard(null);
      newSearchParams.delete(activeCard);
      setSearchParams(newSearchParams);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ display: "inline-block" }}>Requests</h1>
        <button
          className="primary-button"
          onClick={() => navigate('/requests/list')}
        >
          View All Requests
        </button>
      </div>
      
      <p style={{marginTop: "0"}}>
        Add content or inspiration for your next campaign and let us figure out the details.
      </p>

      <div style={{
        backgroundColor: "#fefce8",
        border: "1px solid #fde68a",
        borderRadius: "8px",
        padding: "12px 16px",
        marginBottom: "24px",
        fontSize: "14px",
        color: "#92400e",
      }}>
        <strong>Note:</strong> Requests are for adding content to <em>future</em> newsletters. To request changes to a newsletter that's already been drafted, use the <strong>Feedback</strong> button on that newsletter instead.
      </div>

      {/* Studio Interview Card */}
      {hasInterviews && (
      <div style={{ marginBottom: "32px" }}>
        <p style={{ marginBottom: "16px", fontSize: "14px", color: "#374151" }}>
          Not sure where to start?
        </p>
        <StudioInterviewCard />
      </div>
      )}

      <div>
        <p style={{ marginBottom: "24px", fontSize: "14px", color: "#374151" }}>
          Already know what you want us to include?
        </p>

        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "24px"
        }}>

          {/* Add Voice Note Card */}
          <div onClick={() => handleOptionOpen(IngredientType.VOICE_NOTE)} className="ingredient-card">
            <div className="ingredient-card-icon">
              <MicIcon style={{ width: "32px", height: "32px", color: "#4b5563" }} />
            </div>
            <h3 className="ingredient-card-title">
              Add voice note
            </h3>
            <p className="ingredient-card-description">
              Record to tell us what you want in your content with voice.
            </p>
            <p style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic", marginTop: "8px" }}>
              "I really want to talk about this event we attended last week"
            </p>
          </div>

          {/* Add Link Card */}
          <div onClick={() => handleOptionOpen(IngredientType.LINK)} className="ingredient-card">
            <div className="ingredient-card-icon">
              <LinkIcon style={{ width: "32px", height: "32px", color: "#4b5563" }} />
            </div>
            <h3 className="ingredient-card-title">
              Add link
            </h3>
            <p className="ingredient-card-description">
              Got a link you want us to incorporate into your content? Leave it for us here and we'll handle the rest.
            </p>
            <p style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic", marginTop: "8px" }}>
              "I saw this article I liked that I want to incorporate into the newsletter"
            </p>
          </div>

          {/* Add Text Card */}
          <div onClick={() => handleOptionOpen(IngredientType.TEXT)} className="ingredient-card">
            <div className="ingredient-card-icon">
              <TextIcon style={{ width: "32px", height: "32px", color: "#4b5563" }} />
            </div>
            <h3 className="ingredient-card-title">
              Add text or documents
            </h3>
            <p className="ingredient-card-description">
              Upload documents or paste texts and we'll incorporate it.
            </p>
            <p style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic", marginTop: "8px" }}>
              "Could you repurpose this conversation I had with ChatGPT into some newsletter sections?"
            </p>
          </div>
        </div>
      </div>

      {/* Voice Note Modal */}
      {activeCard === IngredientType.VOICE_NOTE && (
        <VoiceNoteModal onClose={handleOptionClose} />
      )}

      {/* Add Link Modal */}
      {activeCard === IngredientType.LINK && (
        <AddLinkModal 
          onClose={handleOptionClose}
          onSuccess={() => {
            handleOptionClose();
            // Optionally show a success message or refresh
          }}
        />
      )}

      {/* Add Text/Document Modal */}
      {activeCard === IngredientType.TEXT && (
        <AddTextDocumentModal 
          onClose={handleOptionClose}
          onSuccess={() => {
            handleOptionClose();
            // Optionally show a success message or refresh
          }}
        />
      )}
    </div>
  );
};

export default Requests;
